import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, EmptyState } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import { Plus, ClipboardList } from "lucide-react";

export default function RATitles({ adminMode = false }) {
  const { can } = useAuth();
  const canAssign = can("ra:assign_titles");
  const [items, setItems] = useState(null);
  const [available, setAvailable] = useState([]);
  const [ras, setRas] = useState([]);
  const [taxonomy, setTaxonomy] = useState({ industries: [], job_titles: [] });
  const [open, setOpen] = useState(false);
  const [bulk, setBulk] = useState(false);
  const [bulkRas, setBulkRas] = useState([]);
  const [form, setForm] = useState({ title: "", assigned_ra_user_id: "", target_industry: "", target_region: "US", target_location: "", quota: 15, description: "" });

  const load = useCallback(async () => {
    try { const { data } = await api.get("/ra/titles"); setItems(data.items); }
    catch (e) { toast.error(apiErr(e)); setItems([]); }
    if (!adminMode) {
      try { const { data } = await api.get("/ra-dispatch/available"); setAvailable(data.items || []); }
      catch { setAvailable([]); }
    }
  }, [adminMode]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (canAssign) api.get("/ra/team-performance").then(({ data }) => setRas(data.rows.map((r) => ({ id: r.ra_user_id, name: r.ra_name })))).catch(() => {});
  }, [canAssign]);
  useEffect(() => {
    api.get("/ra/taxonomy").then(({ data }) => setTaxonomy(data)).catch(() => {});
  }, []);

  const resetForm = () => {
    setForm({ title: "", assigned_ra_user_id: "", target_industry: "", target_region: "US", target_location: "", quota: 15, description: "" });
    setBulkRas([]); setBulk(false);
  };

  const submit = async () => {
    if (!form.title) { toast.error("Title is required"); return; }
    try {
      if (bulk) {
        if (!bulkRas.length) { toast.error("Select at least one Research Analyst"); return; }
        const { data } = await api.post("/ra/titles/bulk", {
          title: form.title, assigned_ra_user_ids: bulkRas, target_industry: form.target_industry,
          target_region: form.target_region, target_location: form.target_location, quota: form.quota, description: form.description });
        toast.success(`Title assigned to ${data.count} R.A.(s)`);
      } else {
        if (!form.assigned_ra_user_id) { toast.error("Select a Research Analyst"); return; }
        await api.post("/ra/titles", form);
        toast.success("Title assigned");
      }
      setOpen(false); resetForm(); load();
    }
    catch (e) { toast.error(apiErr(e)); }
  };

  const toggleBulkRa = (id) =>
    setBulkRas((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const updateMyStatus = async (id, status) => {
    try {
      await api.post(`/ra-dispatch/mine/${id}/status`, { status });
      toast.success("Status updated");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const claim = async (id) => {
    try {
      await api.post(`/ra-dispatch/${id}/claim`, {});
      toast.success("Claim request sent for admin approval");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <PageHeader title={adminMode ? "Title Assignment" : "My Assigned Titles"}
        subtitle={adminMode ? "Assign research titles to your R.A. team" : "Titles assigned to you for research"}
        actions={adminMode && canAssign && <Button onClick={() => setOpen(true)} data-testid="ra-assign-title-btn" className="gap-1.5 bg-[#255058] hover:bg-[#1c3d43] text-white"><Plus className="h-4 w-4" /> Assign Title</Button>} />

      {items === null ? <Skeleton className="h-40" /> : items.length === 0 ? (
        <EmptyState icon={ClipboardList} title="No titles assigned" description={adminMode ? "Assign a title to get your R.A. team researching." : "You have no titles assigned yet."} />
      ) : (
        <div className="grid md:grid-cols-2 gap-3" data-testid="ra-titles-list">
          {items.map((t) => (
            <Card key={t.id} className="p-4" data-testid={`ra-title-${t.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-display font-semibold">{t.title}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{t.target_industry || "—"} · {t.target_region} · {t.target_location || "—"}</div>
                </div>
                <StatusBadge value={t.status} />
              </div>
              {t.description && <p className="text-sm mt-2 text-muted-foreground">{t.description}</p>}
              <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
                <span>Quota: <b className="text-foreground">{t.quota}</b></span>
                {t.assigned_ra_name && canAssign && <span>R.A.: {t.assigned_ra_name}</span>}
                <span>Due {fmtDate(t.due_date)}</span>
              </div>
              {!adminMode && (
                <div className="mt-3 border-t border-border pt-3">
                  <div className="mb-2 text-xs text-muted-foreground">
                    Work status:{" "}
                    <b className="text-foreground capitalize">
                      {(t.dispatch_status || "not started").replace(/_/g, " ")}
                    </b>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {[["in_progress", "Start working"], ["done", "Mark done"], ["no_jobs", "No jobs"]].map(([s, lbl]) => (
                      <Button
                        key={s}
                        size="sm"
                        variant={t.dispatch_status === s ? "default" : "outline"}
                        onClick={() => updateMyStatus(t.id, s)}
                        data-testid={`my-title-${s}-${t.id}`}
                        className={t.dispatch_status === s ? "bg-[#255058] hover:bg-[#1c3d43] text-white" : ""}
                      >
                        {lbl}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assign a Title</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input
              list="ra-title-taxonomy"
              placeholder="Title / role to research (e.g. VP of Engineering)"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="title-input"
            />
            <datalist id="ra-title-taxonomy">
              {taxonomy.job_titles.map((title) => <option key={title} value={title} />)}
            </datalist>
            <div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
              <span className="text-sm font-medium">Assign to multiple R.A.s</span>
              <Button type="button" size="sm" variant={bulk ? "default" : "outline"}
                onClick={() => setBulk((v) => !v)} data-testid="title-bulk-toggle"
                className={bulk ? "bg-[#255058] hover:bg-[#1c3d43] text-white" : ""}>
                {bulk ? "Bulk: On" : "Bulk: Off"}
              </Button>
            </div>
            {bulk ? (
              <div className="max-h-44 overflow-y-auto rounded-md border border-border p-2 space-y-1" data-testid="title-bulk-ra-list">
                {ras.length === 0 && <div className="text-sm text-muted-foreground p-2">No R.A.s available in your team.</div>}
                {ras.map((r) => (
                  <button type="button" key={r.id} onClick={() => toggleBulkRa(r.id)}
                    data-testid={`title-bulk-ra-${r.id}`}
                    className={`flex w-full items-center justify-between rounded px-2 py-1.5 text-sm ${bulkRas.includes(r.id) ? "bg-[#255058] text-white" : "hover:bg-muted"}`}>
                    <span>{r.name}</span>
                    {bulkRas.includes(r.id) && <span className="text-xs">✓ selected</span>}
                  </button>
                ))}
              </div>
            ) : (
              <Select value={form.assigned_ra_user_id} onValueChange={(v) => setForm({ ...form, assigned_ra_user_id: v })}>
                <SelectTrigger data-testid="title-ra-select"><SelectValue placeholder="Assign to Research Analyst" /></SelectTrigger>
                <SelectContent>{ras.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
              </Select>
            )}
            <div className="grid grid-cols-2 gap-3">
              <Select
                value={form.target_industry || "unspecified"}
                onValueChange={(value) => setForm({
                  ...form,
                  target_industry: value === "unspecified" ? "" : value,
                })}
              >
                <SelectTrigger data-testid="title-industry-select"><SelectValue placeholder="Industry" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unspecified">Industry not specified</SelectItem>
                  {taxonomy.industries.map((industry) => (
                    <SelectItem key={industry} value={industry}>{industry}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={form.target_region} onValueChange={(v) => setForm({ ...form, target_region: v })}>
                <SelectTrigger data-testid="title-region-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["US", "CA", "GCC", "IN"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Location" value={form.target_location} onChange={(e) => setForm({ ...form, target_location: e.target.value })} data-testid="title-location-input" />
              <Input type="number" placeholder="Quota" value={form.quota} onChange={(e) => setForm({ ...form, quota: Number(e.target.value) })} data-testid="title-quota-input" />
            </div>
            <Textarea placeholder="Description / guidance" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="title-description-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="title-cancel">Cancel</Button>
            <Button onClick={submit} data-testid="title-submit" className="bg-[#255058] hover:bg-[#1c3d43] text-white">Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {!adminMode && available.length > 0 && (
        <div className="mt-8" data-testid="ra-available-titles">
          <div className="font-display text-lg font-semibold">Available to claim</div>
          <div className="text-sm text-muted-foreground mb-3">
            Unassigned titles in your team. Request one and an admin will approve it.
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {available.map((t) => (
              <Card key={t.id} className="p-4" data-testid={`ra-available-${t.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-display font-semibold">{t.title}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {t.target_industry || "—"} · {t.target_region} · {t.team_name}
                    </div>
                  </div>
                  <StatusBadge value="unassigned" />
                </div>
                <Button
                  size="sm"
                  disabled={t.claim_pending}
                  onClick={() => claim(t.id)}
                  data-testid={`ra-claim-${t.id}`}
                  className="mt-3 bg-[#255058] hover:bg-[#1c3d43] text-white disabled:opacity-60"
                >
                  {t.claim_pending ? "Requested — pending approval" : "Request to claim"}
                </Button>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
